package com.vamvisions.main

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.vamvisions.main.core.domain.preferences.ColorScheme
import com.vamvisions.main.core.domain.preferences.Language
import com.vamvisions.main.core.presentation.appshell.AppShellAction
import com.vamvisions.main.core.presentation.appshell.AppShellViewModel
import com.vamvisions.main.core.presentation.i18n.stringsFor
import com.vamvisions.main.components.background.IntroBackground
import com.vamvisions.main.components.background.IntroState
import com.vamvisions.main.components.background.LocalIntroState
import com.vamvisions.main.components.markdown.MarkdownStyles
import com.vamvisions.main.core.presentation.navigation.Route
import com.vamvisions.main.di.ViewModelScope
import com.vamvisions.main.di.koinViewModel
import com.vamvisions.main.di.sharedModules
import com.vamvisions.main.i18n.LocalStrings
import com.vamvisions.main.layout.LayoutStyles
import com.vamvisions.main.layout.PageChrome
import com.vamvisions.main.navigation.rememberBrowserHistory
import com.vamvisions.main.pages.NotFoundPage
import com.vamvisions.main.pages.contact.ContactRoot
import com.vamvisions.main.pages.contact.ContactStyles
import com.vamvisions.main.pages.document.DocumentRoot
import com.vamvisions.main.pages.home.HomeRoot
import com.vamvisions.main.pages.home.HomeStyles
import com.vamvisions.main.pages.portfolio.PortfolioRoot
import com.vamvisions.main.pages.portfolio.PortfolioStyles
import com.vamvisions.main.pages.projects.ProjectsRoot
import com.vamvisions.main.pages.projects.ProjectsStyles
import com.vamvisions.main.theme.AppStyleSheet
import com.vamvisions.main.theme.RevealGate
import com.vamvisions.main.theme.initReveal
import kotlinx.browser.document
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.renderComposable
import org.koin.core.context.startKoin

private const val APP_SHELL_SCOPE = "app-shell"

fun main() {
    startKoin { modules(sharedModules) }
    initReveal()
    renderComposable(rootElementId = "root") {
        Style(AppStyleSheet)
        Style(LayoutStyles)
        Style(HomeStyles)
        Style(PortfolioStyles)
        Style(ProjectsStyles)
        Style(ContactStyles)
        Style(MarkdownStyles)
        App()
    }
}

@Composable
private fun App() {
    val history = rememberBrowserHistory()
    ViewModelScope(APP_SHELL_SCOPE) {
        val shell = koinViewModel<AppShellViewModel>()
        val state by shell.state.collectAsState()
        DocumentAttributes(state.colorScheme, state.language)
        RevealGate()
        // Outside the route scope, so the intro and the Home reveal play once per visit and survive navigation.
        val introState = remember { IntroState() }
        IntroBackground(state.colorScheme, introState)

        val onToggleColorScheme = remember(shell) { { shell.onAction(AppShellAction.ToggleColorScheme) } }
        val onLanguageChange = remember(shell) { { language: Language -> shell.onAction(AppShellAction.SetLanguage(language)) } }

        CompositionLocalProvider(LocalStrings provides stringsFor(state.language), LocalIntroState provides introState) {
            val route = history.route
            val chrome = PageChrome(
                currentRoute = route,
                colorScheme = state.colorScheme,
                language = state.language,
                onNavigate = { route -> history.navigate(route) },
                onNavigateToSection = history::navigate,
                onReplace = history::replace,
                onToggleColorScheme = onToggleColorScheme,
                onLanguageChange = onLanguageChange,
            )
            // Each route entry gets its own ViewModels, cleared when the user navigates away.
            ViewModelScope(route) {
                when (route) {
                    Route.Home -> HomeRoot(chrome)
                    Route.Portfolio -> PortfolioRoot(chrome)
                    Route.Projects -> ProjectsRoot(chrome)
                    Route.Contact -> ContactRoot(chrome)
                    is Route.ProjectDocument -> DocumentRoot(route, chrome)
                    is Route.NotFound -> NotFoundPage(chrome)
                }
            }
        }
    }
}

/** Mirrors the shell state onto `<html data-theme>` and `<html lang>`. */
@Composable
private fun DocumentAttributes(colorScheme: ColorScheme, language: Language) {
    LaunchedEffect(colorScheme) {
        document.documentElement?.setAttribute(
            "data-theme",
            when (colorScheme) {
                ColorScheme.LIGHT -> "light"
                ColorScheme.DARK -> "dark"
            },
        )
    }
    LaunchedEffect(language) {
        document.documentElement?.setAttribute("lang", language.code)
    }
}
